/**
 * The words of the Salesforce panel (Settings > Apps > Salesforce, on the new
 * sync). Every customer-facing word of the shell and the overview is here, from
 * the approved design spec (sections 5.7, 6.1 and 6.6), so a wording change is
 * one edit and a spec can check the rules for all of them: plain words, no em
 * or en dashes, "within about 10 minutes" and never "real time".
 *
 * The words of Field mapping live with its model (salesforceMapping.copy.ts).
 * Sync criteria (6.2), Activity in Salesforce (6.4) and Connect (6.5) are here.
 */

import { listOf } from "components/Sync/Salesforce/format";

/** Said by the overview's Reconnect and by Connect: the same window, the same refusal */
const POPUP_BLOCKED =
  "Your browser blocked the Salesforce window. Allow pop-ups for this site, then try again.";

/** The objects the review of a rule change talks about. */
type ReviewObject = "Lead" | "Contact";

/** Every side of the stored rules the screen has words for: Opportunity and Campaign have a card of their own. */
type CopySide = ReviewObject | "Opportunity" | "Campaign";

/** "Leads", "Contacts", "Opportunities", "Campaigns" */
const pluralOf = (side: CopySide): string =>
  side === "Opportunity" ? "Opportunities" : `${side}s`;

/** "1 call", "12 calls" */
const callsOf = (calls: number): string =>
  calls === 1 ? "1 call" : `${calls} calls`;

/** "Leads", "Contacts", "Leads and Contacts" */
const nounsOf = (objects: ReviewObject[]): string =>
  objects.map((object) => `${object}s`).join(" and ");

export const PANEL_COPY = {
  // The shell
  title: "Salesforce",
  screenTitles: {
    criteria: "Sync criteria",
    mapping: "Field mapping",
    activity: "Activity in Salesforce",
  },

  // The footer shared by the screens that save something, and the inline
  // question when the back button is pressed with changes not saved (5.7)
  footer: {
    unsaved: "Changes not saved",
    discard: "Discard changes",
    // the screens save, then the page moves on to the next step (3.4)
    save: "Save and continue",
    saving: "Saving...",
    // the note at the top of a screen whose save did not go through
    saveFailed: "We could not save. Your changes are still here. Try again.",
  },
  leave: {
    question: "Leave without saving?",
    stay: "Stay",
    leave: "Leave",
  },

  // Sync criteria (6.2 of the panel design; 6.7, 6.8 and 7.13 of the criteria
  // design). The words of the review of a rule change (7.13, `criteria.review`)
  // are with the review.
  criteria: {
    lede: "Choose which Leads and Contacts come into Encharge.",
    ledeTip:
      "A record comes in when every rule in one group is true and no Keep out rule is. People who stop matching stay in Encharge and stop updating.",
    cardTitle: {
      Lead: "Leads",
      Contact: "Contacts",
      Opportunity: "Opportunities",
      Campaign: "Campaigns",
    },
    empty: {
      Lead: "All Leads come in.",
      Contact: "All Contacts come in.",
      Opportunity: "All Opportunities are kept up to date.",
      Campaign: "The Campaigns you choose above are copied.",
    },
    // under the Contacts intro, for an org that has Person Accounts
    personAccountsNote:
      "Person Accounts are Contacts here. To bring in or keep out only some of them, use the rule Is Person Account.",
    intro: {
      // sits directly above the rules, with or without Keep out under them: no
      // intro says "below" while a Keep out list is below (M4 of the check)
      one: {
        Lead: "A Lead comes in when all of these rules are true.",
        Contact: "A Contact comes in when all of these rules are true.",
        Opportunity:
          "An Opportunity is kept up to date when all of these rules are true.",
        Campaign:
          "A Campaign the boxes above take is copied when all of these rules are true.",
      },
      groups: {
        Lead: "A Lead comes in when every rule in one of the groups is true.",
        Contact:
          "A Contact comes in when every rule in one of the groups is true.",
        Opportunity:
          "An Opportunity is kept up to date when every rule in one of the groups is true.",
        Campaign:
          "A Campaign the boxes above take is copied when every rule in one of the groups is true.",
      },
      // here "below" is Keep out itself
      keepOutOnly: {
        Lead: "All Leads come in, except the ones kept out below.",
        Contact: "All Contacts come in, except the ones kept out below.",
        Opportunity:
          "All Opportunities are kept up to date, except the ones kept out below.",
        Campaign:
          "The Campaigns you choose above are copied, except the ones kept out below.",
      },
    },
    // operators, not captions: written in capitals (the recorded exception of 6.2)
    and: "AND",
    or: "OR",
    group: (n: number) => `Group ${n}`,
    // read by a screen reader as the box's name
    groupOfAria: (n: number, title: string) => `Group ${n} of ${title}`,
    add: "Add a rule",
    addAria: {
      group: (n: number) => `Add a rule to group ${n}`,
      keepOut: "Add a Keep out rule",
    },
    addGroup: "Add an OR group",
    removeGroup: "Remove group",
    removeGroupAria: (n: number) => `Remove group ${n}`,
    keepOut: {
      title: "Keep out",
      // two or more groups
      hint: {
        Lead:
          "A Lead that matches any rule here stays out, even when a group lets it in.",
        Contact:
          "A Contact that matches any rule here stays out, even when a group lets it in.",
        Opportunity:
          "An Opportunity that matches any rule here is not kept up to date, even when a group lets it in.",
        Campaign:
          "A Campaign that matches any rule here is not copied, even when a group lets it in. A Campaign you pick is always copied.",
      },
      // one group: no group is drawn, so the words do not say "group"
      hintOne: {
        Lead:
          "A Lead that matches any rule here stays out, even when the rules above let it in.",
        Contact:
          "A Contact that matches any rule here stays out, even when the rules above let it in.",
        Opportunity:
          "An Opportunity that matches any rule here is not kept up to date, even when the rules above let it in.",
        Campaign:
          "A Campaign that matches any rule here is not copied, even when the rules above let it in. A Campaign you pick is always copied.",
      },
      start: {
        Lead: "Keep some Leads out",
        Contact: "Keep some Contacts out",
        Opportunity: "Keep some Opportunities out",
        Campaign: "Keep some Campaigns out",
      },
    },
    // read by a screen reader only: the names of a group's rules and of Keep out
    groupAria: "All of these rules",
    keepOutListAria: {
      Lead: "Any of these keeps a Lead out",
      Contact: "Any of these keeps a Contact out",
      Opportunity: "Any of these keeps an Opportunity out",
      Campaign: "Any of these keeps a Campaign out",
    },
    fieldPlaceholder: "Choose a field",
    fieldsLoading: "Loading fields...",
    fieldNoMatch:
      "No field matches. New in Salesforce? Save your rules, then Refresh fields on Field mapping.",
    formulaSuffix: "(formula)",
    inactiveSuffix: "(inactive)",
    valuePlaceholder: "Value",
    valuesPlaceholder: "Values, separated by commas",
    choosePlaceholder: "Choose a value",
    chooseManyPlaceholder: "Choose values",
    boolean: { true: "Checked", false: "Not checked" },
    removeAria: "Remove this rule",
    // read by a screen reader only: the select has no visible label
    conditionAria: "Condition",
    // Check a record (6.9): the link, the strip under it, and the two answers
    // the screen words itself, when there was no answer to show
    check: {
      link: {
        Lead: "Check a Lead",
        Contact: "Check a Contact",
        Opportunity: "Check an Opportunity",
      },
      placeholder: "Email or Salesforce ID",
      // an Opportunity has no email: it is found by its name or its Id
      placeholderOpportunity: "Opportunity name or Salesforce ID",
      hintOpportunity:
        "Uses the switch, the window and the rules on this screen, saved or not.",
      button: "Check",
      hint: "Uses the rules on this screen, saved or not.",
      failed: "We could not check this record. Try again.",
      tooMany:
        "You checked many records in a short time. Try again in a few minutes.",
    },

    // What is wrong with a rule, and what is only worth a look (6.7). A
    // problem blocks Save; a hint never does.
    errors: {
      field: "Choose a field.",
      badField: "This field cannot be used in a rule. Choose another.",
      unknownCondition: "This condition is not supported. Choose another.",
      value: "Add a value.",
      choose: "Choose a value.",
      chooseMany: "Choose at least one value.",
      number: "Enter a number without commas, like 1000000 or 12.5.",
      numberList: "Separate numbers with commas, like 100, 250.",
      date: "Choose a date.",
      dateCompare: (condition: string, object: CopySide) =>
        `${
          condition === "is less than" ? "is less than" : "is more than"
        } does not work on dates, so today this rule lets in every ${object} with a date here. Use is before or is after. This changes which ${pluralOf(
          object
        )} sync.`,
      booleanValue: (object: CopySide) =>
        `A checkbox is Checked or Not checked, so this value never matches. Choose one. This changes which ${pluralOf(
          object
        )} sync.`,
      tooLong: "Use up to 1,000 characters.",
      tooMany: "Use up to 100 values.",
      overRules: (n: number) => `A group can have up to 20 rules. Remove ${n}.`,
      overKeepOut: (n: number) =>
        `Keep out can have up to 20 rules. Remove ${n}.`,
      overTotal: (object: CopySide, n: number) =>
        `${pluralOf(object)} can have up to 50 rules. Remove ${n}.`,
      overGroups: (n: number) => `You can have up to 10 groups. Remove ${n}.`,
    },
    hints: {
      space: "This value starts or ends with a space.",
      notInList: "This is not one of the field's values in Salesforce.",
      multiLegacy:
        "For a multi-select field, is any of also matches records with several values.",
      fieldGone: (object: CopySide) =>
        `Salesforce does not let Encharge read this field, so ${pluralOf(
          object
        )} stopped syncing. Remove this rule, or ask your Salesforce admin to let the connected user read it.`,
      fieldUnlisted:
        "Salesforce did not list this field when fields were last refreshed.",
    },
    // a line that takes the place of an add link once a limit is reached
    limits: {
      rules: "A group can have up to 20 rules.",
      groups: "You can have up to 10 groups.",
      keepOut: "Keep out can have up to 20 rules.",
      total: (object: CopySide) =>
        `${pluralOf(object)} can have up to 50 rules.`,
    },

    fieldsFailed: {
      Lead: "We could not load the Lead fields from Salesforce.",
      Contact: "We could not load the Contact fields from Salesforce.",
      Opportunity: "We could not load the Opportunity fields from Salesforce.",
      Campaign: "We could not load the Campaign fields from Salesforce.",
    },

    // The Opportunity card (Lane 1 of the Opportunity sync): a switch, the window
    // in months, and the same rules as Leads and Contacts. Opportunities are part
    // of the Salesforce package; there is no plan words here
    opportunity: {
      switchLabel: "Sync Opportunities",
      switchTip:
        "Encharge keeps each Opportunity up to date from Salesforce and links it to the people on it and to the company of its Account. Opportunities are read only in Encharge, and only the ones you choose here are kept up to date.",
      hintOn:
        "Encharge keeps Opportunities up to date and links each one to the people on it and to its company.",
      hintOff:
        "Opportunities are not synced. The ones already in Encharge stay as they are.",
      windowLabel: "Keep closed Opportunities up to date for",
      windowAria: "Months a closed Opportunity is kept up to date",
      windowOption: (months: number) =>
        months === 1 ? "1 month" : `${months} months`,
      windowHint:
        "Open Opportunities are always kept up to date. A closed one stops updating this long after its Close Date, unless it is reopened or its Close Date moves back into the window. Nothing is removed.",
      freezeNote:
        "When an Opportunity stops matching, Encharge applies that last change and then stops updating it. It updates again if it matches again. A rule on Stage, Won, Closed or Close Date can stop an Opportunity from updating just as it changes, so use them with care.",
      noFields:
        "Encharge has not read the Opportunity fields from Salesforce yet. Open Field mapping and choose Refresh fields, then come back to add rules.",
      offRules:
        "Switch Opportunities on to choose which ones are kept up to date.",
      // a rule is not finished and Save stops; its card hides the rules while it is off
      offBlocked:
        "A rule here is not finished, so these settings cannot be saved yet. Switch Opportunities on to finish it or take it out.",
      saveFailedPass:
        "Your sync criteria are saved. Encharge could not ask for the read of the extra Opportunities, so they are not read yet. Open Sync Health and run Resync everything to read them.",
    },
    // The Campaigns card (CMP1 of the Campaign sync): a switch that is OFF until the
    // customer chooses, the choice (two boxes, a window and Campaigns picked by
    // hand), and the same rules as the others. The numbers on it come from the
    // server: members and Salesforce calls, and when a removal shows.
    campaign: {
      switchLabel: "Copy Campaigns",
      switchTip:
        "Encharge keeps a read only copy of the Salesforce Campaigns you choose, and one row for each person who is in one of them, so you can make segments from them. It only comes in from Salesforce: nothing is sent back.",
      lede:
        "Keep a copy of the Salesforce Campaigns you choose, and who is in them, so you can make segments from them. Salesforce to Encharge only.",
      hintOff:
        "Campaigns are not copied. Nothing is read from Salesforce until you choose which ones.",
      hintOn: "Encharge copies the Campaigns you choose, and who is in them.",
      // the flows note (S-CP14): the first read starts no flows, later changes do
      flowsNote:
        "The first read of a Campaign starts no flows. After that, flows that start when someone enters or leaves a segment react to Campaign changes.",
      membersNote:
        "Encharge keeps only the members who are already people in Encharge, linked by their Salesforce record. A member who joins Encharge later is added when they arrive.",
      chooseEmpty: "Choose the Campaigns to copy.",
      // the card's summary of the saved choice, in one sentence
      summary: {
        title: "What is copied",
        active: "Campaigns that are Active in Salesforce",
        recent: (months: number) =>
          `Campaigns that started or ended in the last ${
            months === 1 ? "month" : `${months} months`
          }`,
        picked: (count: number) =>
          count === 1
            ? "1 Campaign you picked"
            : `${count} Campaigns you picked`,
        and: ", and ",
      },
      choose: "Choose Campaigns",
      change: "Change the choice",
      // read by a screen reader as the name of the buttons above
      chooseAria: "Choose which Campaigns to copy",
      // when a removal shows: Vince's sentence is built by the server (campaignRemovalNote)
      removalTitle: "When a removal shows",
      removalLoading: "Working out how often removals are checked...",
      removalUnknown:
        "Encharge checks for removed members regularly, less often for a very large setup. You can press Check now to check at once.",
      noFields:
        "Encharge has not read the Campaign fields from Salesforce yet. The rules below use the usual six; open Field mapping and choose Refresh fields to see your own labels and values.",
      offRules: "Switch Campaigns on to choose which ones are copied.",
      offBlocked:
        "A rule here is not finished, so these settings cannot be saved yet. Switch Campaigns on to finish it or take it out.",
      freezeNote:
        "When a Campaign stops matching, or is older than your window, Encharge keeps it and its members as they were last read and stops updating them. Nothing is removed.",

      // Screen 2: Choose Campaigns
      picker: {
        title: "Which Campaigns?",
        close: "Done",
        activeLabel: "Campaigns that are Active in Salesforce",
        recentLabel: "Campaigns that started or ended in the last",
        recentAria: "Months back a Campaign started or ended",
        windowOption: (months: number) =>
          months === 1 ? "1 month" : `${months} months`,
        recentHint:
          "An Active Campaign with no end date counts as recent. Older Campaigns stop updating; their members stay as they were last read.",
        picksTitle: "Campaigns I pick",
        picksHint:
          "A Campaign you pick is always copied, whatever the boxes above and your rules say. Untick both boxes to copy only the ones you pick.",
        searchLabel: "Search Campaigns",
        searchPlaceholder: "Search by name",
        loading: "Loading your Campaigns from Salesforce...",
        loadFailed: "We could not list your Campaigns.",
        retry: "Try again",
        // a Salesforce that answers with an empty list; one without the marketing feature gets the server's line
        empty: "This Salesforce has no Campaigns yet.",
        noMatch: "No Campaign matches.",
        listCount: (shown: number, matching: number, total: number) =>
          matching === total
            ? `Showing ${shown} of ${total} Campaigns.`
            : `Showing ${shown} of ${matching} matching Campaigns (${total} in all).`,
        showMore: (more: number) => `Show ${more} more`,
        truncated:
          "Salesforce has more Campaigns than this list holds. It shows the active ones first, then the newest, up to 2,000.",
        pickedCount: (count: number) =>
          count === 1 ? "1 picked" : `${count} picked`,
        notListed: (count: number) =>
          count === 1
            ? "1 Campaign you picked is not in this list (deleted in Salesforce, or past the first 2,000). It stays picked."
            : `${count} Campaigns you picked are not in this list (deleted in Salesforce, or past the first 2,000). They stay picked.`,
        pick: (name: string) => `Pick ${name}`,
        unpick: (name: string) => `Take ${name} off the picks`,
        remove: "Remove",
        active: "Active",
        inactive: "Inactive",
        members: (count: number) =>
          count === 1 ? "1 member" : `${count} members`,
        membersUpTo: (count: string) => `up to ${count} members`,
        noDates: "No dates",
        dates: (from: string | null, to: string | null) =>
          from && to
            ? `${from} to ${to}`
            : from
            ? `From ${from}`
            : to
            ? `Until ${to}`
            : "No dates",
        type: "Type",
        status: "Status",
        columnName: "Campaign",
        columnType: "Type",
        columnStatus: "Status",
        columnDates: "Dates",
        columnMembers: "Members",
      },
    },
    tryAgain: "Try again",

    // The screen (7.13). `loadFailed` is followed by `tryAgain`.
    loadFailed: "We could not load your sync criteria.",
    // "Someone" when the name is not known; the time is left out when the
    // server did not say it
    conflict: (name: string | null, time: string) =>
      `${name || "Someone"} saved other sync criteria${
        time ? ` at ${time}` : ""
      } while you were editing. Your changes are not saved. Reload to start from the saved rules.`,
    reloadSaved: "Reload saved rules",
    // {time} always carries its date
    restoreNote: (time: string) =>
      `These are the rules from before the change on ${time}. Save to put them back. People who came in since then stay in Encharge.`,
    restoreNone: "There are no earlier rules to restore.",

    // The review that opens when rules are saved on a sync that is running
    // (7.13, `criteria.review`). The count's own sentences that hold a time or a
    // field name ("We could not count. ...") are the server's, shown as they come.
    review: {
      title: "Save new sync criteria?",
      closeAria: "Close",
      // "Leads", "Contacts" or "Leads and Contacts": the objects the change touches
      counting: (objects: ReviewObject[]) =>
        `Checking your ${nounsOf(
          objects
        )} in Salesforce against the new rules. Nothing has changed yet.`,
      countingWait:
        "On a large Salesforce org this can take a while. You can wait for the numbers, or save now without them.",
      // the same line while the Campaign read is refused: the review has no button to save without the numbers
      countingWaitRefused:
        "On a large Salesforce org this can take a while. You can wait for the numbers.",
      countingButton: "Counting...",
      saveWithout: "Save without the numbers",
      stalled:
        "Counting has not moved for a long time. Close this and try again.",
      tile: {
        start: (object: ReviewObject) => `${object}s start matching`,
        stop: (object: ReviewObject) => `${object}s stop matching`,
        of: (matchNow: string) => `of ${matchNow} that match now`,
      },
      // the numbers come formatted
      checked: (counts: { object: ReviewObject; checked: string }[]) =>
        `Checked ${counts
          .map(({ object, checked }) => `${checked} ${object}s`)
          .join(" and ")} in Salesforce. Nothing has changed yet.`,
      whatHappens: (objects: ReviewObject[]) => {
        const nouns = nounsOf(objects);
        const stop =
          objects.length === 1 ? `${nouns} that` : "Leads and Contacts that";
        return `Nobody is deleted or unsubscribed, and ${stop} stop matching stay in Encharge but stop updating. This happens for each record when it next changes in Salesforce, or all at once if you run Resync everything on Sync Health.`;
      },
      nothing: (objects: ReviewObject[]) =>
        `No ${objects.join(" or ")} starts or stops matching with these rules.`,
      caption: (objects: ReviewObject[]) =>
        objects.length === 2
          ? "Counts leave out converted Leads, and Leads and Contacts with no email."
          : objects[0] === "Lead"
          ? "Counts leave out converted Leads and Leads with no email."
          : "Counts leave out Contacts with no email.",
      big: {
        stop: (object: ReviewObject, count: string, matchNow: string) =>
          `This is a big change: ${count} of the ${matchNow} ${object}s that match now stop matching. Encharge stops updating them and stops writing to them in Salesforce.`,
        start: (object: ReviewObject, count: string) =>
          `This is a big change: ${count} ${object}s start matching. They come into Encharge, and the flows below may start for them.`,
        none: (object: ReviewObject) =>
          `These rules match no ${object}. Every ${object} in Encharge would stop updating.`,
      },
      // The part of the review about Opportunities (Lane 1): one question to
      // Salesforce, so there is no progress and no tiles
      opportunity: {
        title: "Opportunities",
        counting: "Counting the Opportunities in Salesforce...",
        countingWait:
          "This takes a moment. You can wait for the number, or save now without it.",
        // the same line while the Campaign read is refused: there is no button to save without the number
        countingWaitRefused:
          "This takes a moment. You can wait for the number.",
        // the numbers come formatted; the days are YYYY-MM-DD
        band: (count: string, calls: number, from: string, to: string) =>
          `A longer window adds the closed Opportunities with a Close Date from ${from} up to the day before ${to}. Salesforce has ${count} of them, and reading them takes about ${callsOf(
            calls
          )}.`,
        bandEmpty: (from: string, to: string) =>
          `No closed Opportunity has a Close Date from ${from} up to the day before ${to}, so nothing more is read.`,
        windowRules: (count: string, calls: number, from: string) =>
          `Encharge reads the open Opportunities and the ones closed since ${from} again, to apply the new rules. Salesforce has ${count} of them, and reading them takes about ${callsOf(
            calls
          )}.`,
        windowSwitchedOn: (count: string, calls: number, from: string) =>
          `Switching Opportunities back on reads the open Opportunities and the ones closed since ${from}. Salesforce has ${count} of them, and reading them takes about ${callsOf(
            calls
          )}.`,
        windowEmpty: (from: string) =>
          `Salesforce has no open Opportunity and none closed since ${from}, so nothing is read.`,
        quiet:
          "Reading them starts no flows. Nothing is removed: an Opportunity that no longer matches is kept as it is and stops updating.",
        failed:
          "We could not count the Opportunities. Salesforce did not answer. You can still save.",
      },
      // The part of the review about Campaigns (CMP1, Screen 3): one query to
      // Salesforce, so there is no progress and no tiles. The numbers come
      // formatted; members are an upper bound.
      campaign: {
        title: "Campaigns",
        counting: "Counting the Campaigns in Salesforce...",
        countingWait:
          "This takes a moment. You can wait for the numbers, or save now without them.",
        // Line 1 of the answer: what is read and, when the day's allowance is
        // known, what that costs, in one line. "This reads up to about 48,200
        // members in 14 Campaigns, about 152 Salesforce calls (1 percent of your
        // daily allowance)." With no cost it ends after the Campaigns.
        reads: (
          members: string,
          campaigns: string,
          picked: number,
          cost?: string
        ) =>
          `This reads up to about ${members} members in ${campaigns}${
            picked > 0 ? ` (${picked} picked by hand)` : ""
          }${cost ? `, ${cost}` : ""}.`,
        readsBand: (campaigns: string, members: string, cost?: string) =>
          `A longer window adds ${campaigns}, with up to about ${members} members${
            cost ? `, ${cost}` : ""
          }.`,
        cost: (calls: string, share: string) =>
          `about ${calls} Salesforce calls (${share} of your daily allowance)`,
        costOne: (share: string) =>
          `about 1 Salesforce call (${share} of your daily allowance)`,
        // Line 3 of the answer
        quiet: "The first read starts no flows, and nothing is removed.",
        nothingNew:
          "This change reads no Campaign again. Nothing is removed: a Campaign that falls out of your choice is kept as last read and stops updating.",
        picksNotListed: (count: number) =>
          count === 1
            ? "1 Campaign you picked is not in Salesforce's list now. It stays picked."
            : `${count} Campaigns you picked are not in Salesforce's list now. They stay picked.`,
        truncated:
          "Salesforce has more Campaigns than the 2,000 this list holds, so the numbers may be a little low.",
        // CD1: the read would take the day past 80 percent
        refused: (used: string, after: string) =>
          `You have used about ${used} of today's Salesforce calls, and reading these Campaigns would take the day to about ${after}. Encharge stops at 80 percent to keep the rest for your other tools, so choose fewer Campaigns or save again tomorrow.`,
        refusedNoUsage: (after: string) =>
          `Reading these Campaigns would take today's Salesforce calls to about ${after}. Encharge stops at 80 percent to keep the rest for your other tools, so choose fewer Campaigns or save again tomorrow.`,
        failed:
          "We could not work out what the Campaigns would cost. You can still save.",
        refusedSave: "Too many calls for today",
      },
      keepEditing: "Keep editing",
      save: "Save rules",
      saveBig: (kind: "stop" | "start", object: ReviewObject, count: string) =>
        `Save: ${count} ${object}s ${kind} matching`,
      saving: "Saving...",
      saveFailed: "We could not save. Your changes are still here. Try again.",
      // the line for a count that could not be asked for at all; the server's
      // own lines (paused, reconnect, switched off, too many, a field, ...) come
      // as they are
      cannotCount: (reason: string) =>
        `We could not count. ${reason} You can still save. We do not know how many Leads and Contacts this changes.`,
      reasonOwn: "Encharge ran into a problem.",
      changedWhileCounting:
        "Someone saved other sync criteria while we counted. Close this and reload the rules to see them.",
      notJudged: {
        field: (object: ReviewObject, field: string, matchNew: string) =>
          `The saved ${object} rules use ${field}, which Salesforce does not let Encharge read, so we cannot say which ${object}s start or stop matching. The new rules match ${matchNew} ${object}s.`,
        unreadable: (object: ReviewObject, matchNew: string) =>
          `The saved ${object} rules cannot be read, so we cannot say which ${object}s start or stop matching. The new rules match ${matchNew} ${object}s.`,
      },
      replaced:
        "Another count was started for this account, so this one stopped. Close this and press Save again to count your rules.",
      readFailed: "We could not get the count's progress. Trying again.",
      resyncRunning:
        "A resync is running. Saving new rules may stop it. What it applied stays applied.",
      comparison:
        "A resync comparison is open on Sync Health. Saving new rules makes it out of date, and you would compare again.",
      fullRead:
        "Encharge is still reading Salesforce from the start. Records it has already read follow the new rules only when they change.",
    },
  },

  // Activity in Salesforce (6.4)
  activity: {
    lede:
      "Show what people do in Encharge on their Lead or Contact in Salesforce.",
    ledeTip:
      "Each event is added to the Encharge activity list on the Lead or Contact page. People with only an Account are skipped.",
    app: {
      label: "Encharge app in Salesforce",
      installed: "Installed",
      missing: "Not installed",
      checking: "Checking...",
      install: "Install app",
      installBusy: "Waiting for Salesforce...",
      hintMissing: "Needed only for activity. Syncing works without it.",
      hintMissingButOn:
        "Activity cannot reach Salesforce until the app is installed.",
      notYet: "The app is not installed yet.",
      checkFailed: "We could not check the app.",
      checkAgain: "Check again",
    },
    email: {
      label: "Email activity",
      hint:
        "Deliveries, opens, clicks, replies, bounces, unsubscribes and spam reports.",
    },
    page: { label: "Page visits" },
    form: { label: "Form submissions" },
    // "Needs {Site Tracking} on your website."
    siteTrackingBefore: "Needs ",
    siteTracking: "Site Tracking",
    siteTrackingAfter: " on your website.",
  },

  // Connect (6.5): an account that has not connected Salesforce yet
  connect: {
    title: "Salesforce account",
    tag: "Not connected",
    hint:
      "A Salesforce window opens. Sign in and click Allow. Nothing syncs until you start it in step 5.",
    // for an account that was disconnected: it may have started syncing before, so
    // the step 5 sentence is not true for it (the card beside it is `setup.next.again`)
    hintAgain: "A Salesforce window opens. Sign in and click Allow.",
    button: "Connect Salesforce",
    waiting: "Waiting for Salesforce...",
    failed:
      "Salesforce did not connect. Try again, or ask your Salesforce admin to allow the Encharge app.",
    blocked: POPUP_BLOCKED,
  },

  // After a switch to another Salesforce (SR13): the field lists of the new one.
  // Two short sentences at most for each note; the names are the list.
  fields: {
    // A Refresh fields is owed: no field of the old Salesforce is listed meanwhile
    owed: {
      line:
        "Encharge is reading the fields of your new Salesforce. If this message stays, press Refresh fields.",
      button: "Refresh fields",
      busy: "Refreshing...",
      failed:
        "Encharge could not read the fields from Salesforce. Try again in a moment.",
    },
    // Start syncing waits for that read: no first read runs on the old Salesforce's lists
    startWaits:
      "Start syncing waits until Encharge has read the fields of your new Salesforce.",
    // The new Salesforce lacks fields the mapping holds or a rule uses
    missing: {
      line: (count: number): string =>
        count === 1
          ? "Your new Salesforce does not have 1 field that you use."
          : `Your new Salesforce does not have ${count} fields that you use.`,
      advice: "Choose another field or remove it.",
      where: {
        mapped: "in field mapping",
        rules: "in sync rules",
        both: "in field mapping and sync rules",
      },
      more: (count: number): string =>
        count === 1 ? "And 1 more." : `And ${count} more.`,
    },
  },

  // The overview (6.1)
  home: {
    // Lede and the callout at the top, by what the health says
    lede: {
      setup:
        "Check these settings, then start syncing. You can change them later.",
      lastSync: (when: string) => `Last sync ${when}.`,
      waiting: "Waiting for the first sync.",
      running: "A sync is running now.",
    },
    openHealth: "Open Sync Health",
    reconnectCallout: "Salesforce needs to be reconnected.",
    unreadable: "We could not read the sync status.",
    tryAgain: "Try again",

    criteria: {
      all: "All Leads and Contacts.",
      some: (leads: string, contacts: string) =>
        `Leads: ${leads}. Contacts: ${contacts}.`,
      // what one object's rules come to: "all", "2 rules", "2 groups", with
      // Keep out appended: "all, 2 Keep out rules", "2 groups, 1 Keep out rule"
      part: ({
        groups,
        rules,
        keepOut,
      }: {
        groups: number;
        rules: number;
        keepOut: number;
      }) => {
        const include =
          groups <= 0
            ? "all"
            : groups === 1
            ? rulesOf(rules)
            : `${groups} groups`;
        if (keepOut <= 0) return include;
        return `${include}, ${
          keepOut === 1 ? "1 Keep out rule" : `${keepOut} Keep out rules`
        }`;
      },
    },
    mapping: {
      oneWay: "Salesforce to Encharge only.",
      none: "No field sends changes to Salesforce.",
      one: "1 field sends changes to Salesforce.",
      many: (n: number) => `${n} fields send changes to Salesforce.`,
    },
    activity: {
      off: "Off.",
      on: (kinds: string[]) => `On for ${listOf(kinds)}.`,
      kinds: {
        email: "emails",
        page: "page visits",
        form: "form submissions",
      },
      appMissing: "On, but the Encharge app is not installed.",
    },
    connection: {
      reconnect: "Reconnect",
      waiting: "Waiting for Salesforce...",
      blocked: POPUP_BLOCKED,
      disconnect: "Disconnect",
      disconnectHint:
        "Encharge loses access to your Salesforce org and syncing stops. Until you connect again, Salesforce steps in your flows fail, and unsubscribes and changes made in Encharge are not sent to Salesforce later. Your settings are kept.",
      // what a Disconnect does to Campaign data (S-CP7): nothing yet, a clear on the next switch
      disconnectCampaigns:
        "Campaigns and their members stay as they are for now. They are cleared when you connect a different Salesforce, and people stay.",
      cancel: "Cancel",
    },

    // The What syncs card
    whatSyncs: {
      title: "What syncs",
      people: {
        label: "People",
        fromSalesforce: "From Salesforce",
        both: "Some fields both ways",
        hint:
          "Leads and Contacts that match your sync criteria and have an email.",
        // an org that has Person Accounts: they are Contacts here
        hintPersonAccounts:
          "Leads and Contacts (Person Accounts included) that match your sync criteria and have an email.",
      },
      // only for an org that has Person Accounts
      personAccounts: {
        label: "Person Accounts",
        personOnly: "Person only",
        personAndCompany: "Person and company",
        hintPersonOnly:
          "Each one comes in as the Contact Salesforce keeps for it, with a yes or no on Person Account. It gets no company. You can change this on the Fields step.",
        hintPersonAndCompany:
          "Each one comes in as the Contact Salesforce keeps for it, and also gets a company named after the person. You can change this on the Fields step.",
        hintNoCompanies:
          "Each one comes in as the Contact Salesforce keeps for it, with a yes or no on Person Account. Your plan has no Companies.",
      },
      companies: {
        label: "Companies",
        fromSalesforce: "From Salesforce",
        both: "Some fields both ways",
        notOnPlan: "Not on your plan",
        hint: "Only Accounts linked to a synced Lead or Contact.",
        hintNoCompanies: "Salesforce Accounts do not become companies.",
        hintCopy: "Account fields are copied onto each person instead.",
      },
      links: {
        label: "Links",
        value: "From Salesforce",
        hint:
          "Salesforce decides them. A link you add in Encharge to a Salesforce company is removed.",
      },
      unsubscribes: {
        label: "Unsubscribes",
        value: "Both ways",
        hint:
          "Salesforce never resubscribes someone who unsubscribed in Encharge.",
        bounce: " A bounce in Salesforce unsubscribes too.",
        tip:
          "An unsubscribe in Encharge ticks Email Opt Out on every Lead and Contact with that email, even with Salesforce to Encharge only on. A resubscribe clears it. Salesforce can clear only the unsubscribes it made.",
      },
      deletions: {
        label: "Deletions",
        value: "Not synced",
        hint: "A record deleted in Salesforce stays in Encharge.",
      },
      guide: "Read the Salesforce guide",
    },

    // The footer while a new connection waits to be started
    start: "Start syncing",
    starting: "Starting...",
    startFailed: "We could not start syncing. Try again.",
    // Above Start syncing, only when a live Entered segment or Left segment flow exists
    // (Vince, 2026-10-18): the first read starts no flows of its own, but a segment flow
    // on a Salesforce field can start for the people it brings in or updates
    segmentFlowsWarning:
      "Segment flows on Salesforce fields can start for people the first read brings in or updates. To avoid that, add those flows after the first read has finished.",

    // Shown once, at the top, after something was done
    notice: {
      started: "Syncing has started. A large Salesforce org can take a while.",
      mapping: "Field mapping saved.",
      mappingSends: "Only changes made from now on go to Salesforce.",
      criteriaSyncing:
        "Sync criteria saved. Each Lead and Contact follows the new rules when it next changes in Salesforce. To apply them to every record now, run Resync everything.",
      // a save the review skipped because who matches cannot change (7.3)
      criteriaSame:
        "Sync criteria saved. This does not change which Leads and Contacts match.",
      criteriaReady: "Sync criteria saved.",
      // only the Opportunity side changed (Lane 1): nothing more to read, or a read asked for, or a read that could not be asked for
      opportunity:
        "Sync criteria saved. Each Opportunity follows the new rules when it next changes in Salesforce. Nothing is removed.",
      opportunityReading:
        "Sync criteria saved. Encharge is reading the extra Opportunities from Salesforce now, in the background. Reading them starts no flows.",
      opportunityFailed:
        "Sync criteria saved, but Encharge could not ask for the read of the extra Opportunities. Run Resync everything to read them.",
      // only the Campaign side changed (CMP1): nothing more to read, or a read asked for, or a read that could not be asked for
      campaign:
        "Sync criteria saved. Campaigns follow the new choice as they change in Salesforce, and nothing is removed.",
      campaignReading:
        "Sync criteria saved. Encharge is reading the Campaigns from Salesforce in the background, and reading them starts no flows.",
      campaignFailed:
        "Sync criteria saved, but Encharge could not ask for the read of the Campaigns. Switch Campaigns off, save, then switch them on and save again to ask once more.",
      // only the Opportunity and Campaign sides changed, and both reads were asked for
      opportunityAndCampaignReading:
        "Sync criteria saved. Encharge is reading the extra Opportunities and the Campaigns in the background, and reading them starts no flows.",
      // ... and neither read could be asked for: each has its own way to ask again
      opportunityAndCampaignFailed:
        "Sync criteria saved, but Encharge could not ask for the extra Opportunities or the Campaigns. Run Resync everything for the Opportunities, and for the Campaigns switch them off, save, then switch them on and save again.",
      activity: "Activity saved.",
    },
  },

  // The setup page: five steps, a bar on top and a footer under the step
  // (design 6, "The frame"). The words of the footer buttons that already exist
  // (Saving..., Discard changes, Start syncing, Connect Salesforce, Try again,
  // Save and stop copying) are not repeated here: the model reads them from where
  // they are.
  setup: {
    backToApps: "All apps",
    barAria: "Salesforce setup steps",
    stepOf: (n: number) => `Step ${n} of 5:`,
    // the label of a step in the bar, and its title under it; step 5 says
    // "Syncing" once syncing has started (2.1)
    labels: {
      connect: "Connect",
      criteria: "Who syncs",
      mapping: "Fields",
      activity: "Activity",
      start: "Start syncing",
      started: "Syncing",
    },
    titles: {
      connect: "Connect Salesforce",
      criteria: "Sync criteria",
      mapping: "Field mapping",
      activity: "Activity in Salesforce",
      start: "Start syncing",
      started: "Syncing",
    },
    // the tag beside the title of step 4
    optional: "Optional",
    // under a label in the bar (2.2); a step that is done has none
    captions: {
      attn: "Needs attention",
      check: "Check this",
      optional: "Optional",
      stopped: "Stopped",
      locked: "Connect first",
    },
    // what a screen reader adds to a step's name (2.5)
    hidden: {
      done: (n: number) => `Step ${n} of 5, done`,
      todo: (n: number) => `Step ${n} of 5, not done yet`,
      optional: (n: number) => `Step ${n} of 5, optional`,
      stopped: (n: number) => `Step ${n} of 5, stopped`,
      attn: (n: number) => `Step ${n} of 5, needs attention`,
      check: (n: number) => `Step ${n} of 5, check this`,
      locked: (n: number) => `Step ${n} of 5, connect Salesforce first`,
    },
    // the line under the title: what to do here
    ledes: {
      connectNew:
        "Connect the Salesforce account Encharge should sync with. Every other step opens once it is connected.",
      connected: "The Salesforce account Encharge syncs with.",
      mapping: "Choose which Salesforce fields fill which Encharge fields.",
      activitySkip: "You can skip this step.",
      startHeld:
        "Nothing syncs until you start. Check what will be used, then start syncing. You can change every step later.",
      startOk:
        "Every step is set and Salesforce is syncing. To change a setting, click Change beside it.",
    },
    // the footer (3.2)
    footer: {
      back: "Back",
      backTo: (label: string) => `Back: ${label}`,
      continue: "Continue",
      saveAndContinue: "Save and continue",
      skip: "Skip for now",
      reconnect: "Reconnect Salesforce",
      reconnectBusy: "Waiting for Salesforce...",
      looksRight: "This looks right",
    },
    // the one instruction in the footer's status spot on a step that needs a
    // look and has no big button that says the fix (3.2)
    statusLines: {
      criteriaField: (field: string) =>
        `Remove the marked rule and save, or ask your admin to let Encharge read ${field}.`,
      mappingMigrated: "Check the fields, then click This looks right.",
      criteriaUnreadable: "Check the rules, then save them again.",
      activityApp: "Install the app, or switch Activity off, then save.",
      syncHealth: "Open Sync Health to see what to do.",
      // not in the design: fields Salesforce no longer lists, with no migrated mapping
      mappingLeftOut: "Check the fields that cannot be read, then save.",
    },
    // under the lede while the bar shows circles only (2.7)
    phoneLine: {
      attn: "Also needs attention:",
      check: "Also check:",
    },
    // the question in the footer after a click in the bar or on the phone line (3.7):
    // "Leave Field mapping without saving and go to step 4, Activity?". It comes in
    // three parts because "step {n}," is one no-wrap span, so a line never ends on
    // "step" with its number on the next line. After Back the existing "Leave
    // without saving?" (`leave.question`) stands: its target is the step before.
    leaveTo: (title: string, n: number, label: string) => ({
      before: `Leave ${title} without saving and go to `,
      step: `step ${n},`,
      after: ` ${label}?`,
    }),
    // shown once at the top of a step (3.8); the other notices are `home.notice`
    notice: {
      connected: "Salesforce is connected.",
    },
    // "Step 2:" before the name of a step in a row of Your setup, What will be
    // used and What happens next
    stepPrefix: (n: number) => `Step ${n}:`,
    // step 1, connected (2.6): the account card. The card's title is
    // `connect.title`; the buttons are `home.connection`.
    connection: {
      tagConnected: "Connected",
      tagReconnect: "Reconnect needed",
      hint:
        "Reconnect with the same Salesforce org. Connecting another org starts the sync over for that org.",
    },
    // the card beside Connect: fixed words, no read from Salesforce (2.6)
    next: {
      title: "What happens next",
      criteria: "All Leads and Contacts, unless you add rules.",
      mapping:
        "Salesforce to Encharge only. You can choose fields to send back.",
      activity: "Optional. Needs the Encharge app in Salesforce.",
      start: "Nothing syncs until you press Start syncing.",
      againTitle: "When you connect again",
      again:
        "Connect the same Salesforce org to carry on with the settings you had. Connecting another org starts the sync over for that org.",
    },
    // the callout on every step while the login must be renewed (4.4); it starts
    // with `home.reconnectCallout`
    reconnectMore: "Nothing syncs until you do.",
    // the callout at the top of step 2 for a rule Salesforce refuses (4.3, mock
    // m6b). A rule list Encharge cannot read has no words here: the callout says
    // the server's own sentence (S6).
    criteriaCallout: {
      fieldGone: (field: string, object: CopySide, keepOutOnly: boolean) =>
        `Salesforce does not let Encharge read ${field}, which ${
          keepOutOnly ? "a Keep out rule" : "a rule"
        } uses, so ${pluralOf(
          object
        )} stopped syncing. The rule is marked below.`,
    },
    // the three settings with a way to change each: one card, two titles (2.6, 4.2)
    summary: {
      yourSetup: "Your setup",
      willBeUsed: "What will be used",
      change: "Change",
    },
    // step 5 after the start (4.2): the Status card
    status: {
      title: "Status",
      lastSync: "Last sync",
      health: "Sync Health",
      healthHint:
        "Counts, the last 24 hours, Resync everything and Compare a record.",
    },
    // hidden words after a link that opens a new tab, with the external-link icon (6)
    newTab: " (opens in a new tab)",
    // what the page says in place of itself
    states: {
      loadFailed: "We could not load your Salesforce settings.",
      tryAgain: "Try again",
      noAppsPermission: "You don't have permission to modify connected apps.",
      notOnPlan: "This integration is not available on your plan.",
    },
  },
};

/** "all", "1 rule", "2 rules": what a Leads or Contacts count says in a summary. */
function rulesOf(count: number): string {
  if (count <= 0) return "all";
  return count === 1 ? "1 rule" : `${count} rules`;
}
